// src/components/Home.js
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Link } from 'react-router-dom';
import Header from '../layout/Header';

const STYLES = `
  @import url('https://fonts.googleapis.com/css2?family=Sora:wght@300;400;500;600;700;800;900&family=DM+Sans:ital,opsz,wght@0,9..40,300;0,9..40,400;0,9..40,500;0,9..40,600;1,9..40,300&display=swap');
  html { scroll-behavior: smooth; }
  body { overflow-x: hidden; }
  .home-root * { font-family: 'DM Sans', sans-serif; box-sizing: border-box; }

  /* ── Orbs ── */
  @keyframes orb-drift {
    0%,100% { transform: translate(0,0) scale(1); }
    33%      { transform: translate(50px,-40px) scale(1.1); }
    66%      { transform: translate(-30px,25px) scale(0.92); }
  }
  .orb { position:fixed; border-radius:50%; filter:blur(90px); animation: orb-drift 18s ease-in-out infinite; pointer-events:none; z-index:0; }
  .orb-1 { width:600px;height:600px;background:radial-gradient(circle,#6c63ff,transparent 68%);opacity:.22;top:-200px;left:-180px; }
  .orb-2 { width:420px;height:420px;background:radial-gradient(circle,#a78bfa,transparent 68%);opacity:.18;bottom:-100px;right:-80px;animation-delay:-6s; }
  .orb-3 { width:320px;height:320px;background:radial-gradient(circle,#38bdf8,transparent 68%);opacity:.15;top:45%;left:60%;animation-delay:-11s; }
  .orb-4 { width:260px;height:260px;background:radial-gradient(circle,#f472b6,transparent 68%);opacity:.1;top:30%;right:5%;animation-delay:-3s; }
  .orb-dk-1{opacity:.18;background:radial-gradient(circle,#4338ca,transparent 68%);}
  .orb-dk-2{opacity:.14;background:radial-gradient(circle,#7c3aed,transparent 68%);}
  .orb-dk-3{opacity:.10;background:radial-gradient(circle,#0ea5e9,transparent 68%);}
  .orb-dk-4{opacity:.08;background:radial-gradient(circle,#db2777,transparent 68%);}

  /* ── Dot grid ── */
  .dot-grid { position:fixed;inset:0;z-index:1;pointer-events:none;
    background-image:radial-gradient(circle,rgba(108,99,255,.14) 1px,transparent 1px);
    background-size:32px 32px; }

  /* ── Gradient text ── */
  .grad    { background:linear-gradient(135deg,#6c63ff 0%,#a78bfa 55%,#38bdf8 100%); -webkit-background-clip:text; -webkit-text-fill-color:transparent; background-clip:text; }
  .grad-dk { background:linear-gradient(135deg,#a78bfa 0%,#c4b5fd 55%,#7dd3fc 100%); -webkit-background-clip:text; -webkit-text-fill-color:transparent; background-clip:text; }

  /* ── Hero stagger ── */
  @keyframes hi { from{opacity:0;transform:translateY(38px)} to{opacity:1;transform:translateY(0)} }
  .h1{animation:hi .65s cubic-bezier(.22,.68,0,1.1) .08s both}
  .h2{animation:hi .65s cubic-bezier(.22,.68,0,1.1) .22s both}
  .h3{animation:hi .65s cubic-bezier(.22,.68,0,1.1) .38s both}
  .h4{animation:hi .65s cubic-bezier(.22,.68,0,1.1) .54s both}
  .h5{animation:hi .65s cubic-bezier(.22,.68,0,1.1) .70s both}
  .h6{animation:hi .65s cubic-bezier(.22,.68,0,1.1) .86s both}

  /* ── Float emoji ── */
  @keyframes fa{0%,100%{transform:translateY(0) rotate(-3deg)}50%{transform:translateY(-10px) rotate(3deg)}}
  @keyframes fb{0%,100%{transform:translateY(0) rotate(2deg)}50%{transform:translateY(-8px) rotate(-3deg)}}
  @keyframes fc{0%,100%{transform:translateY(0)}50%{transform:translateY(-12px)}}
  .fa{animation:fa 4s ease-in-out infinite}
  .fb{animation:fb 3.6s ease-in-out infinite;animation-delay:.8s}
  .fc{animation:fc 5s ease-in-out infinite;animation-delay:1.6s}

  /* ── Scroll reveal ── */
  @keyframes reveal-up   { from{opacity:0;transform:translateY(44px)}  to{opacity:1;transform:translateY(0)} }
  @keyframes reveal-left { from{opacity:0;transform:translateX(-32px)} to{opacity:1;transform:translateX(0)} }
  @keyframes reveal-scale{ from{opacity:0;transform:scale(.93)}        to{opacity:1;transform:scale(1)} }
  .reveal{opacity:0;} .reveal.up{opacity:0;} .reveal.left{opacity:0;} .reveal.scale{opacity:0;}
  .reveal.up.visible    { animation:reveal-up    .7s cubic-bezier(.22,.68,0,1.1) both; }
  .reveal.left.visible  { animation:reveal-left  .7s cubic-bezier(.22,.68,0,1.1) both; }
  .reveal.scale.visible { animation:reveal-scale .6s cubic-bezier(.22,.68,0,1.1) both; }
  .reveal.visible       { animation:reveal-up    .65s cubic-bezier(.22,.68,0,1.1) both; }

  /* ── Glass card ── */
  .glass-card { backdrop-filter:blur(12px); -webkit-backdrop-filter:blur(12px); transition:transform .28s cubic-bezier(.34,1.56,.64,1),box-shadow .28s ease; }
  .glass-card:hover { transform:translateY(-6px) scale(1.015); }
  .glass-card.no-hover:hover { transform:none; }

  /* ── Button shine ── */
  @keyframes shine{from{left:-120%}to{left:120%}}
  .btn-glow { position:relative; overflow:hidden; }
  .btn-glow::after { content:'';position:absolute;top:0;left:-120%;width:60%;height:100%;background:linear-gradient(90deg,transparent,rgba(255,255,255,.3),transparent);transform:skewX(-20deg); }
  .btn-glow:hover::after { animation:shine .55s ease; }

  /* ── Shimmer stat numbers ── */
  @keyframes shimmer { 0%{background-position:-200% center} 100%{background-position:200% center} }
  .stat-num { background:linear-gradient(90deg,#6c63ff 0%,#a78bfa 40%,#38bdf8 60%,#6c63ff 100%); background-size:200% auto; -webkit-background-clip:text; -webkit-text-fill-color:transparent; background-clip:text; animation:shimmer 3.5s linear infinite; }
  .stat-num-dk { background:linear-gradient(90deg,#a78bfa 0%,#c4b5fd 40%,#7dd3fc 60%,#a78bfa 100%); background-size:200% auto; -webkit-background-clip:text; -webkit-text-fill-color:transparent; background-clip:text; animation:shimmer 3.5s linear infinite; }

  /* ── Plan card ── */
  .plan-card { transition:transform .32s cubic-bezier(.34,1.56,.64,1),box-shadow .32s ease; }
  .plan-card:hover { transform:translateY(-10px); }
  .plan-featured::before {
    content:'Most Popular'; position:absolute; top:-13px; left:50%; transform:translateX(-50%);
    background:linear-gradient(90deg,#6c63ff,#8b5cf6); color:#fff;
    font-size:11px; font-weight:700; letter-spacing:.08em; padding:3px 14px; border-radius:99px;
    white-space:nowrap; font-family:'Sora',sans-serif;
  }

  /* ══════════════════════════════════════
     AGENCIES — floating card carousel
  ══════════════════════════════════════ */

  /* Continuous horizontal scroll for two cloned rows */
  @keyframes scroll-left  { from{transform:translateX(0)} to{transform:translateX(-50%)} }
  @keyframes scroll-right { from{transform:translateX(-50%)} to{transform:translateX(0)} }

  .testi-track { overflow:hidden; position:relative; }
  .testi-track::before,
  .testi-track::after {
    content:''; position:absolute; top:0; width:120px; height:100%; z-index:2; pointer-events:none;
  }
  .testi-track::before { left:0; background:linear-gradient(90deg, var(--fade-color, #f0f0fb), transparent); }
  .testi-track::after  { right:0; background:linear-gradient(-90deg, var(--fade-color, #f0f0fb), transparent); }

  .testi-belt { display:flex; width:max-content; gap:20px; padding:10px 0; }
  .testi-belt.go-left  { animation:scroll-left  38s linear infinite; }
  .testi-belt.go-right { animation:scroll-right 42s linear infinite; }
  .testi-belt:hover    { animation-play-state:paused; }

  /* Individual floating card */
  @keyframes card-bob {
    0%,100% { transform: translateY(0) rotate(0deg); }
    33%      { transform: translateY(-5px) rotate(.4deg); }
    66%      { transform: translateY(3px) rotate(-.3deg); }
  }
  .testi-card-wrap { flex-shrink:0; width:300px; }
  .testi-card-inner {
    border-radius:24px; padding:24px; cursor:default;
    transition:transform .3s cubic-bezier(.34,1.56,.64,1), box-shadow .3s ease;
    animation: card-bob var(--bob-d, 5s) ease-in-out infinite;
    animation-delay: var(--bob-del, 0s);
  }
  .testi-card-inner:hover {
    transform:translateY(-10px) scale(1.03) !important;
    animation-play-state:paused;
  }

  /* ══════════════════════════════════════
     ABOUT — rotating message reel
  ══════════════════════════════════════ */
  .msg-reel { position:relative; overflow:hidden; }

  @keyframes msg-enter { from{opacity:0;transform:translateY(28px) scale(.95)} to{opacity:1;transform:translateY(0) scale(1)} }
  @keyframes msg-exit  { from{opacity:1;transform:translateY(0) scale(1)} to{opacity:0;transform:translateY(-28px) scale(.95)} }

  .msg-slide-in  { animation:msg-enter .5s cubic-bezier(.22,.68,0,1.1) both; }
  .msg-slide-out { animation:msg-exit  .4s ease both; }

  /* typing cursor */
  @keyframes blink { 0%,100%{opacity:1} 50%{opacity:0} }
  .cursor { display:inline-block; width:2px; height:1em; background:currentColor; margin-left:3px; vertical-align:middle; animation:blink 1s step-end infinite; }

  /* progress bar for message */
  @keyframes msg-progress { from{width:0%} to{width:100%} }
  .msg-bar { height:2px; border-radius:99px; animation:msg-progress var(--dur,5s) linear both; }

  /* ── Ticker ── */
  @keyframes ticker{from{transform:translateX(0)}to{transform:translateX(-50%)}}
  .ticker-inner { animation:ticker 28s linear infinite; white-space:nowrap; display:flex; }
  .ticker-inner:hover { animation-play-state:paused; }

  /* ── Section divider ── */
  .sdiv { width:60px;height:3px;border-radius:99px;background:linear-gradient(90deg,#6c63ff,#a78bfa);margin:0 auto 20px; }

  /* ── FAQ ── */
  .faq-body { overflow:hidden; transition:max-height .38s ease, opacity .3s ease; }

  /* ── Particle stars ── */
  @keyframes twinkle { 0%,100%{opacity:.15;transform:scale(1)} 50%{opacity:.7;transform:scale(1.4)} }
  .star { position:absolute; border-radius:50%; background:#a78bfa; pointer-events:none; animation:twinkle var(--d,3s) ease-in-out infinite; animation-delay:var(--del,0s); }

  /* ── Glow pulse CTA ── */
  @keyframes glow-pulse { 0%,100%{box-shadow:0 24px 70px rgba(108,99,255,.35)} 50%{box-shadow:0 24px 90px rgba(108,99,255,.6)} }
  .cta-glow { animation:glow-pulse 3s ease-in-out infinite; }

  /* stagger delays */
  .sd1{animation-delay:.05s}.sd2{animation-delay:.12s}.sd3{animation-delay:.19s}
  .sd4{animation-delay:.26s}.sd5{animation-delay:.33s}.sd6{animation-delay:.40s}

  .home-root ::-webkit-scrollbar { width:5px; }
  .home-root ::-webkit-scrollbar-track { background:transparent; }
  .home-root ::-webkit-scrollbar-thumb { background:rgba(108,99,255,.3); border-radius:99px; }
`;

/* ═══ DATA ═══ */
const NAV_SECTIONS = [
  {id:'hero',label:'Home'},{id:'products',label:'Products'},{id:'plans',label:'Plans'},
  {id:'agencies',label:'Agencies'},{id:'security',label:'Security'},
  {id:'about',label:'About'},{id:'support',label:'Support'},
];

const PRODUCTS = [
  {icon:'🤖',fa:'fa',title:'BotBuilder',tag:'Core Platform',desc:'Drag-and-drop bot creation studio. Build permit, inspection, and compliance bots in hours — no code needed.',color:'from-violet-500/15 to-indigo-500/10',border:'border-violet-400/20'},
  {icon:'⚡',fa:'fb',title:'FlowEngine',tag:'Automation',desc:'Connect Accela workflows to 150+ integrations. Trigger actions, notifications, and approvals automatically.',color:'from-blue-500/15 to-cyan-500/10',border:'border-blue-400/20'},
  {icon:'📊',fa:'fc',title:'InsightBoard',tag:'Analytics',desc:'Real-time dashboards for permit volumes, SLA compliance, staff performance, and citizen satisfaction scores.',color:'from-emerald-500/15 to-green-500/10',border:'border-emerald-400/20'},
  {icon:'🔔',fa:'fa',title:'NotifyIQ',tag:'Communications',desc:'Smart multi-channel notifications — SMS, email, and in-app — that keep citizens and staff in sync.',color:'from-orange-500/15 to-amber-500/10',border:'border-orange-400/20'},
  {icon:'🛡️',fa:'fb',title:'ComplianceAI',tag:'Security',desc:'Automated compliance checks against local, state, and federal codes updated in real-time.',color:'from-rose-500/15 to-pink-500/10',border:'border-rose-400/20'},
  {icon:'🌐',fa:'fc',title:'CitizenPortal',tag:'Self-Service',desc:'A branded self-service portal for citizens to apply, pay, schedule, and track — 24/7 without staff help.',color:'from-teal-500/15 to-cyan-500/10',border:'border-teal-400/20'},
];

/* ── Updated pricing: 1-week trial, $399/mo ── */
const PLANS = [
  {name:'Starter',price:'$0',period:'/mo',badge:null,desc:'Perfect for small agencies getting started with automation.',features:['Up to 3 bots','500 transactions/mo','Email support','Basic analytics','CitizenPortal (read-only)'],cta:'Get Started Free',ctaStyle:'outline',trial:null},
  {name:'Professional',price:'$399',period:'/mo',badge:'featured',desc:'The go-to plan for mid-size county and city agencies.',features:['Up to 25 bots','25,000 transactions/mo','Priority support (4hr SLA)','Full InsightBoard','FlowEngine (50 integrations)','NotifyIQ (SMS + Email)','SSO / SAML'],cta:'Start 7-Day Free Trial',ctaStyle:'primary',trial:'7-day free trial'},
  {name:'Enterprise',price:'Custom',period:'',badge:null,desc:'Full-scale deployment for large or multi-agency governments.',features:['Unlimited bots & transactions','Dedicated success manager','99.99% SLA uptime','ComplianceAI included','On-premise or GovCloud','Custom integrations','Audit logs & FedRAMP'],cta:'Contact Sales',ctaStyle:'outline',trial:null},
];

/* ── Testimonials: doubled for seamless belt ── */
const TESTIMONIALS_RAW = [
  {agency:'City of Riverside, CA',role:'IT Director',name:'Marcus T.',avatar:'MT',color:'from-violet-500 to-indigo-600',quote:'Botable cut our permit processing time from 14 days to under 48 hours. Our citizens couldn\'t be happier.',stars:5,bob:'5.2s',bobDel:'0s'},
  {agency:'Gwinnett County, GA',role:'Accela Business Analyst',name:'Sarah K.',avatar:'SK',color:'from-blue-500 to-cyan-600',quote:'The FlowEngine integrations saved us 3 FTEs worth of manual data-entry work every single week.',stars:5,bob:'4.8s',bobDel:'0.6s'},
  {agency:'COBB County, GA',role:'Deputy Director',name:'James R.',avatar:'JR',color:'from-emerald-500 to-green-600',quote:'InsightBoard gave leadership real visibility for the first time. We now make decisions on data, not gut feel.',stars:5,bob:'5.6s',bobDel:'1.2s'},
  {agency:'City of Mesa, AZ',role:'Accela Developer',name:'Priya N.',avatar:'PN',color:'from-orange-500 to-rose-500',quote:'Building our first inspection bot took less than a day. The drag-and-drop studio is incredibly intuitive.',stars:5,bob:'4.5s',bobDel:'0.3s'},
  {agency:'State of Colorado',role:'IT Administrator',name:'Derek W.',avatar:'DW',color:'from-pink-500 to-fuchsia-600',quote:'ComplianceAI flagged a code conflict we\'d missed for two years. The ROI was immediate.',stars:5,bob:'5.9s',bobDel:'0.9s'},
  {agency:'GovTech Solutions',role:'Solutions Architect',name:'Lena M.',avatar:'LM',color:'from-teal-500 to-sky-500',quote:'We\'ve deployed Botable for 8 clients. Every single one has renewed. The platform just works.',stars:5,bob:'5.1s',bobDel:'1.5s'},
];

const SECURITY_ITEMS = [
  {icon:'🔐',title:'End-to-End Encryption',desc:'AES-256 at rest and TLS 1.3 in transit. Your data is protected at every layer.'},
  {icon:'🏛️',title:'FedRAMP Ready',desc:'Architecture designed to meet FedRAMP Moderate baseline for government cloud services.'},
  {icon:'📋',title:'SOC 2 Type II Certified',desc:'Independently audited annually for security, availability, and confidentiality controls.'},
  {icon:'👁️',title:'Full Audit Trails',desc:'Every action, every login — immutable logs retained 7 years for records requirements.'},
  {icon:'🌍',title:'GovCloud Hosting',desc:'Runs in AWS GovCloud (US) with geographic redundancy and 99.99% uptime SLA.'},
  {icon:'🔑',title:'SSO & MFA Enforced',desc:'SAML 2.0, OIDC, and FIDO2 hardware key support. MFA enforced across all accounts.'},
];

/* ── Team with their personal moving messages ── */
const TEAM = [
  {name:'Alexandra Chen',role:'CEO & Co-Founder',avatar:'AC',color:'from-violet-500 to-indigo-600',bio:'Former VP Engineering at Accela. 15 years in GovTech.',
    messages:[
      "Our mission: make government services as delightful as the best apps. 🏛️",
      "We started Botable because the pain was real — and fixable.",
      "Every line of code is dedicated to the citizen waiting at the counter.",
      "GovTech doesn't have to mean slow. We're proving it. 🚀",
    ]},
  {name:'Marcus Williams',role:'CTO & Co-Founder',avatar:'MW',color:'from-blue-500 to-cyan-600',bio:'Ex-Google infrastructure engineer. Built systems for 500M+ users.',
    messages:[
      "99.99% uptime isn't a goal — it's a promise we build toward daily. ⚡",
      "Distributed systems taught me: resilience is designed, not hoped for.",
      "We handle 2M+ transactions because we obsess over the boring stuff.",
      "Great infrastructure should be invisible. Citizens just see speed. 🔧",
    ]},
  {name:'Priya Sharma',role:'Chief Product Officer',avatar:'PS',color:'from-emerald-500 to-teal-600',bio:'Led product at Tyler Technologies for 8 years.',
    messages:[
      "The best government software feels like it was made for humans. 💚",
      "I've sat with 200+ permit clerks. Their pain shaped every feature.",
      "Citizen UX isn't a nice-to-have. It's the whole product.",
      "A 2-minute permit check vs 2 days? That's what good design does. 📐",
    ]},
  {name:'Jordan Lee',role:'VP of Sales',avatar:'JL',color:'from-orange-500 to-amber-600',bio:'$50M+ in government software deals across 30 states.',
    messages:[
      "Agencies don't buy software. They buy outcomes. We deliver both. 🤝",
      "30 states, 50+ agencies — every one had a unique story.",
      "The best pitch? When the demo does the talking for you.",
      "ROI within 90 days, or we work with you until it happens. 💪",
    ]},
];

const FAQS = [
  {q:'How long does implementation take?',a:'Most agencies are live in 2–4 weeks. Our team handles data migration, bot config, and staff training. Enterprise averages 6–8 weeks.'},
  {q:'Does Botable work with existing Accela setups?',a:'Yes — built specifically for Accela. We support all versions and use native APIs, so there\'s no disruption to existing configurations.'},
  {q:'What happens to our data if we cancel?',a:'You retain full ownership. On cancellation we provide a complete export (JSON, CSV, PDF) and permanently delete all copies within 30 days.'},
  {q:'Is there a free trial?',a:'The Professional plan includes a 7-day free trial — no credit card required. The Starter plan is free forever for low-volume agencies.'},
  {q:'Are you compliant with public records laws?',a:'Yes. Audit logs, retention policies, and export tools satisfy FOIA, state public records acts, and local retention schedules.'},
];

const STATS = [
  {value:'50+',label:'Government Agencies'},
  {value:'2M+',label:'Transactions Processed'},
  {value:'99.9%',label:'Platform Uptime'},
  {value:'48hr',label:'Avg. Permit Time'},
];

const STARS_BG = Array.from({length:18},(_,i)=>({
  w: 2+Math.random()*3,
  top:`${5+Math.random()*88}%`,
  left:`${2+Math.random()*96}%`,
  d:`${2.2+Math.random()*3}s`,
  del:`${Math.random()*4}s`,
}));

/* ═══ Testimonial Card ═══ */
const TestiCard = ({ t, dk }) => (
  <div className="testi-card-wrap">
    <div
      className={`testi-card-inner border ${dk?'bg-[#12122e]/85 border-white/10':'bg-white/85 border-gray-200/70'}`}
      style={{
        '--bob-d': t.bob, '--bob-del': t.bobDel,
        boxShadow: dk ? '0 8px 32px rgba(0,0,0,.35)' : '0 8px 32px rgba(108,99,255,.1)',
      }}
    >
      <div className="flex items-center gap-3 mb-4">
        <div className={`w-10 h-10 rounded-xl bg-gradient-to-br ${t.color} flex items-center justify-center text-white font-black text-xs flex-shrink-0`}
          style={{fontFamily:'Sora,sans-serif'}}>{t.avatar}</div>
        <div>
          <p className={`font-bold text-sm leading-tight ${dk?'text-white':'text-gray-900'}`} style={{fontFamily:'Sora,sans-serif'}}>{t.name}</p>
          <p className={`text-xs ${dk?'text-violet-400':'text-violet-600'}`}>{t.agency}</p>
        </div>
      </div>
      <div className={`text-2xl leading-none mb-2 ${dk?'text-violet-700':'text-violet-300'}`}>"</div>
      <p className={`text-xs leading-relaxed italic mb-3 ${dk?'text-gray-300':'text-gray-700'}`}>{t.quote}</p>
      <div className="flex gap-0.5">{[...Array(t.stars)].map((_,s)=><span key={s} className="text-amber-400 text-xs">★</span>)}</div>
    </div>
  </div>
);

/* ═══ Rotating Message Box ═══ */
const MSG_DURATION = 4200; // ms per message

const RotatingMessages = ({ member, dk }) => {
  const [idx, setIdx] = useState(0);
  const [phase, setPhase] = useState('in'); // 'in' | 'out'
  const [barKey, setBarKey] = useState(0);

  useEffect(() => {
    const exitTimer = setTimeout(() => setPhase('out'), MSG_DURATION - 500);
    const nextTimer = setTimeout(() => {
      setIdx(i => (i + 1) % member.messages.length);
      setPhase('in');
      setBarKey(k => k + 1);
    }, MSG_DURATION);
    return () => { clearTimeout(exitTimer); clearTimeout(nextTimer); };
  }, [idx, member.messages.length]);

  return (
    <div className={`mt-3 rounded-2xl border p-4 min-h-[90px] flex flex-col justify-between overflow-hidden relative ${
      dk?'bg-[#1a1a3a]/60 border-white/10':'bg-violet-50/60 border-violet-200/50'
    }`}>
      {/* progress bar */}
      <div className={`absolute top-0 left-0 h-[2px] rounded-full msg-bar ${dk?'bg-violet-500':'bg-violet-400'}`}
        key={barKey} style={{'--dur':`${MSG_DURATION}ms`}} />

      <div className="msg-reel flex-1 flex items-start">
        <p
          className={`text-xs leading-relaxed ${phase==='in'?'msg-slide-in':'msg-slide-out'} ${dk?'text-gray-300':'text-gray-700'}`}
          key={`${member.name}-${idx}`}
        >
          {member.messages[idx]}
          {phase==='in' && <span className={`cursor ${dk?'bg-gray-300':'bg-gray-600'}`}/>}
        </p>
      </div>

      {/* dot indicators */}
      <div className="flex gap-1 mt-3">
        {member.messages.map((_,i)=>(
          <div key={i} className={`h-1 rounded-full transition-all duration-300 ${
            i===idx
              ? `w-4 ${dk?'bg-violet-400':'bg-violet-500'}`
              : `w-1 ${dk?'bg-gray-700':'bg-gray-300'}`
          }`}/>
        ))}
      </div>
    </div>
  );
};

/* ═══════════════ HOME ═══════════════ */
export default function Home() {
  const [isDarkMode,    setIsDarkMode]    = useState(false);
  const [activeSection, setActiveSection] = useState('hero');
  const [openFaq,       setOpenFaq]       = useState(null);
  const dk = isDarkMode;
  const HEADER_H = 106; // 62px main + 44px spy row

  useEffect(() => {
    const saved = localStorage.getItem('theme');
    const prefersDark = window.matchMedia?.('(prefers-color-scheme: dark)').matches;
    setIsDarkMode(saved === 'dark' || (!saved && prefersDark));
  }, []);

  const toggleDarkMode = () => {
    const n = !isDarkMode; setIsDarkMode(n); localStorage.setItem('theme', n?'dark':'light');
  };

  /* Scroll-spy */
  useEffect(() => {
    const io = new IntersectionObserver(
      entries => entries.forEach(e => { if(e.isIntersecting) setActiveSection(e.target.id); }),
      { rootMargin:'-25% 0px -65% 0px', threshold:0 }
    );
    NAV_SECTIONS.forEach(({id})=>{ const el=document.getElementById(id); if(el) io.observe(el); });
    return () => io.disconnect();
  }, []);

  /* Reveal on scroll */
  useEffect(() => {
    const io = new IntersectionObserver(
      entries => entries.forEach(e=>{ if(e.isIntersecting) e.target.classList.add('visible'); }),
      { threshold:0.07 }
    );
    document.querySelectorAll('.reveal').forEach(el=>io.observe(el));
    return () => io.disconnect();
  }, []);

  const scrollTo = id => {
    const el = document.getElementById(id);
    if (!el) return;
    const top = el.getBoundingClientRect().top + window.scrollY - HEADER_H - 12;
    window.scrollTo({ top, behavior:'smooth' });
  };

  /* Fade color for testimonial track masks */
  const fadeColor = dk ? 'rgba(9,9,28,1)' : 'rgba(240,240,251,1)';

  return (
    <>
      <style>{STYLES}</style>
      <div className={`home-root flex flex-col min-h-screen transition-colors duration-500 overflow-x-hidden ${
        dk ? 'bg-gradient-to-br from-[#060612] via-[#0a0a1c] to-[#0f0c22]'
           : 'bg-gradient-to-br from-[#f0f0fb] via-[#f8f8ff] to-[#eceef8]'
      }`}>

        <div style={{position:'fixed',inset:0,zIndex:0,pointerEvents:'none',overflow:'hidden'}}>
          <div className={`orb orb-1 ${dk?'orb-dk-1':''}`}/><div className={`orb orb-2 ${dk?'orb-dk-2':''}`}/>
          <div className={`orb orb-3 ${dk?'orb-dk-3':''}`}/><div className={`orb orb-4 ${dk?'orb-dk-4':''}`}/>
        </div>
        <div className={`dot-grid ${dk?'opacity-20':'opacity-50'}`}/>

        <Header isDarkMode={isDarkMode} toggleDarkMode={toggleDarkMode} activeSection={activeSection}/>
        <div style={{height:HEADER_H+'px',flexShrink:0}}/>

        <main style={{position:'relative',zIndex:5}}>

          {/* ════ HERO ════ */}
          <section id="hero" className="relative min-h-[92vh] flex flex-col items-center justify-center text-center px-6 pt-8 pb-24 overflow-hidden">
            {STARS_BG.map((s,i)=>(
              <div key={i} className="star" style={{width:s.w,height:s.w,top:s.top,left:s.left,'--d':s.d,'--del':s.del,opacity:.15}}/>
            ))}
            <span className="h1 inline-flex items-center gap-2 mb-6 px-4 py-1.5 rounded-full text-xs font-semibold tracking-widest uppercase border backdrop-blur-sm"
              style={{background:dk?'rgba(108,99,255,.12)':'rgba(108,99,255,.08)',borderColor:dk?'rgba(108,99,255,.3)':'rgba(108,99,255,.2)',color:dk?'#a78bfa':'#6c63ff',fontFamily:'Sora,sans-serif'}}>
              <span className="w-1.5 h-1.5 rounded-full bg-green-400 animate-pulse"/> Government Automation Platform
            </span>
            <h1 className="h2 text-5xl md:text-7xl lg:text-[82px] font-black leading-[1.02] tracking-tight mb-7 max-w-5xl" style={{fontFamily:'Sora,sans-serif'}}>
              <span className={dk?'text-white':'text-gray-900'}>Automate your </span>
              <span className={dk?'grad-dk':'grad'}>Government</span><br/>
              <span className={dk?'text-white':'text-gray-900'}>workflows with </span>
              <span className={dk?'grad-dk':'grad'}>BOTABLE</span>
            </h1>
            <p className={`h3 text-lg md:text-xl max-w-2xl leading-relaxed mb-12 ${dk?'text-gray-300':'text-gray-600'}`}>
              Deploy intelligent bots for permits, inspections, licensing, and compliance. Built specifically for Accela — live in weeks, not years.
            </p>
            <div className="h4 flex flex-col sm:flex-row gap-4 justify-center items-center mb-20">
              <Link to="/signup" className="btn-glow inline-flex items-center gap-2 py-4 px-10 bg-gradient-to-r from-[#6c63ff] to-[#8b5cf6] hover:from-[#5b52ee] hover:to-[#7c3aed] text-white font-bold text-base rounded-2xl hover:scale-[1.05] active:scale-95 shadow-2xl hover:shadow-violet-500/40 transition-all duration-200" style={{fontFamily:'Sora,sans-serif'}}>
                🚀 Get Started — It's Free
              </Link>
              <button onClick={()=>scrollTo('products')} className={`inline-flex items-center gap-2 py-4 px-10 font-semibold text-base rounded-2xl border-2 transition-all duration-200 hover:scale-[1.03] active:scale-95 ${dk?'border-gray-700 text-gray-300 hover:bg-white/5 hover:border-gray-500':'border-gray-300 text-gray-700 hover:bg-white hover:border-violet-300 hover:text-violet-700'}`} style={{fontFamily:'Sora,sans-serif'}}>
                Explore Products ↓
              </button>
            </div>
            <div className="h5 grid grid-cols-2 md:grid-cols-4 gap-5 max-w-3xl w-full">
              {STATS.map((s,i)=>(
                <div key={i} className={`glass-card no-hover rounded-2xl border p-5 text-center sd${i+1} ${dk?'bg-white/5 border-white/8':'bg-white/60 border-gray-200/60'}`}
                  style={{boxShadow:dk?'0 4px 24px rgba(0,0,0,.3)':'0 4px 24px rgba(108,99,255,.07)'}}>
                  <p className={`text-3xl font-black mb-1 ${dk?'stat-num-dk':'stat-num'}`} style={{fontFamily:'Sora,sans-serif'}}>{s.value}</p>
                  <p className={`text-xs font-medium uppercase tracking-widest ${dk?'text-gray-500':'text-gray-400'}`}>{s.label}</p>
                </div>
              ))}
            </div>
            <div className="h6 mt-14 flex flex-col items-center gap-2 opacity-40">
              <span className={`text-xs uppercase tracking-widest ${dk?'text-gray-500':'text-gray-400'}`} style={{fontFamily:'Sora,sans-serif'}}>Scroll to explore</span>
              <div className={`w-px h-8 ${dk?'bg-gray-600':'bg-gray-400'}`} style={{animation:'hi 1.5s ease-in-out infinite alternate'}}/>
            </div>
          </section>

          {/* ════ PRODUCTS ════ */}
          <section id="products" className="py-28 px-6">
            <div className="max-w-6xl mx-auto">
              <div className="reveal up text-center mb-16">
                <div className="sdiv"/>
                <p className={`text-xs uppercase tracking-widest font-semibold mb-3 ${dk?'text-violet-400':'text-violet-600'}`} style={{fontFamily:'Sora,sans-serif'}}>Platform</p>
                <h2 className={`text-4xl md:text-5xl font-black mb-5 ${dk?'text-white':'text-gray-900'}`} style={{fontFamily:'Sora,sans-serif'}}>Everything your agency needs</h2>
                <p className={`text-lg max-w-xl mx-auto ${dk?'text-gray-400':'text-gray-600'}`}>Six fully integrated products that work together — or stand alone.</p>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
                {PRODUCTS.map((p,i)=>(
                  <div key={i} className={`reveal up glass-card rounded-3xl border p-7 bg-gradient-to-br ${p.color} ${p.border} sd${i+1}`}
                    style={{boxShadow:dk?'0 8px 32px rgba(0,0,0,.25)':'0 8px 32px rgba(108,99,255,.06)'}}>
                    <div className="flex items-start justify-between mb-5">
                      <span className={`text-4xl ${p.fa}`}>{p.icon}</span>
                      <span className={`text-xs font-semibold px-3 py-1 rounded-full ${dk?'bg-white/10 text-gray-300':'bg-white/70 text-gray-500'}`} style={{fontFamily:'Sora,sans-serif'}}>{p.tag}</span>
                    </div>
                    <h3 className={`text-lg font-bold mb-2 ${dk?'text-white':'text-gray-900'}`} style={{fontFamily:'Sora,sans-serif'}}>{p.title}</h3>
                    <p className={`text-sm leading-relaxed ${dk?'text-gray-400':'text-gray-600'}`}>{p.desc}</p>
                    <button className={`mt-5 text-xs font-semibold flex items-center gap-1 transition-colors ${dk?'text-violet-400 hover:text-violet-300':'text-violet-600 hover:text-violet-700'}`} style={{fontFamily:'Sora,sans-serif'}}>Learn more →</button>
                  </div>
                ))}
              </div>
            </div>
          </section>

          {/* Ticker */}
          <div className={`py-4 border-y overflow-hidden ${dk?'border-white/5 bg-white/2':'border-gray-200/50 bg-white/30'}`}>
            <div className="ticker-inner">
              {[0,1].map(r=>(
                <div key={r} className="flex items-center gap-10 pr-10">
                  {['Building Permits','Code Enforcement','Business Licensing','Inspections','Zoning Reviews','Public Works','Environmental Health','Fire Safety','Property Assessment','Planning Applications'].map((t,i)=>(
                    <span key={i} className={`flex items-center gap-2 text-sm font-medium whitespace-nowrap ${dk?'text-gray-500':'text-gray-400'}`} style={{fontFamily:'Sora,sans-serif'}}>
                      <span className={`w-1.5 h-1.5 rounded-full ${dk?'bg-violet-500':'bg-violet-400'}`}/>{t}
                    </span>
                  ))}
                </div>
              ))}
            </div>
          </div>

          {/* ════ PLANS — updated $399, 7-day trial ════ */}
          <section id="plans" className="py-28 px-6">
            <div className="max-w-5xl mx-auto">
              <div className="reveal up text-center mb-16">
                <div className="sdiv"/>
                <p className={`text-xs uppercase tracking-widest font-semibold mb-3 ${dk?'text-violet-400':'text-violet-600'}`} style={{fontFamily:'Sora,sans-serif'}}>Pricing</p>
                <h2 className={`text-4xl md:text-5xl font-black mb-5 ${dk?'text-white':'text-gray-900'}`} style={{fontFamily:'Sora,sans-serif'}}>Simple, transparent pricing</h2>
                <p className={`text-lg max-w-lg mx-auto ${dk?'text-gray-400':'text-gray-600'}`}>No hidden fees. No per-seat surprises.</p>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6 items-start">
                {PLANS.map((pl,i)=>(
                  <div key={i} className={`reveal scale plan-card glass-card rounded-3xl border p-8 sd${i+1} ${pl.badge?'plan-featured relative mt-5':''} ${
                    pl.badge
                      ? dk?'bg-gradient-to-b from-violet-900/40 to-indigo-900/30 border-violet-500/40':'bg-gradient-to-b from-violet-50 to-indigo-50/50 border-violet-400/40'
                      : dk?'bg-white/5 border-white/8':'bg-white/70 border-gray-200/60'
                  }`}
                    style={{boxShadow:pl.badge?(dk?'0 16px 50px rgba(108,99,255,.3)':'0 16px 50px rgba(108,99,255,.15)'):(dk?'0 8px 32px rgba(0,0,0,.25)':'0 8px 24px rgba(0,0,0,.06)')}}>
                    <p className={`text-sm font-bold mb-3 ${dk?'text-gray-300':'text-gray-500'}`} style={{fontFamily:'Sora,sans-serif'}}>{pl.name}</p>
                    <div className="flex items-end gap-1 mb-1">
                      <span className={`text-4xl font-black ${dk?'text-white':'text-gray-900'}`} style={{fontFamily:'Sora,sans-serif'}}>{pl.price}</span>
                      <span className={`text-sm mb-2 ${dk?'text-gray-500':'text-gray-400'}`}>{pl.period}</span>
                    </div>
                    {/* 7-day trial badge */}
                    {pl.trial && (
                      <div className="mb-3">
                        <span className={`inline-flex items-center gap-1.5 text-xs font-bold px-3 py-1 rounded-full ${dk?'bg-green-900/40 text-green-300 border border-green-700/40':'bg-green-50 text-green-700 border border-green-200'}`}>
                          🎉 {pl.trial} — no credit card
                        </span>
                      </div>
                    )}
                    <p className={`text-sm mb-7 ${dk?'text-gray-400':'text-gray-600'}`}>{pl.desc}</p>
                    <ul className="space-y-3 mb-8">
                      {pl.features.map((f,j)=>(
                        <li key={j} className={`flex items-start gap-2.5 text-sm ${dk?'text-gray-300':'text-gray-700'}`}>
                          <span className="text-green-400 mt-0.5 flex-shrink-0">✓</span>{f}
                        </li>
                      ))}
                    </ul>
                    <Link to="/signup" className={`btn-glow block w-full text-center py-3.5 rounded-xl font-bold text-sm transition-all hover:scale-[1.03] active:scale-95 ${
                      pl.ctaStyle==='primary'
                        ?'bg-gradient-to-r from-[#6c63ff] to-[#8b5cf6] text-white shadow-lg hover:shadow-violet-500/30'
                        :dk?'border-2 border-gray-600 text-gray-300 hover:border-violet-500 hover:text-violet-300':'border-2 border-gray-300 text-gray-700 hover:border-violet-400 hover:text-violet-700'
                    }`} style={{fontFamily:'Sora,sans-serif'}}>{pl.cta}</Link>
                  </div>
                ))}
              </div>
              <p className={`text-center text-sm mt-8 ${dk?'text-gray-500':'text-gray-400'}`}>
                Professional plan: <strong className={dk?'text-gray-300':'text-gray-600'}>7-day free trial</strong> — no credit card required.
              </p>
            </div>
          </section>

          {/* ════ AGENCIES — floating belt ════ */}
          <section id="agencies" className={`py-28 ${dk?'bg-white/2':'bg-gray-50/50'}`}>
            <div className="max-w-6xl mx-auto px-6">
              <div className="reveal up text-center mb-14">
                <div className="sdiv"/>
                <p className={`text-xs uppercase tracking-widest font-semibold mb-3 ${dk?'text-violet-400':'text-violet-600'}`} style={{fontFamily:'Sora,sans-serif'}}>Agencies Love Botable</p>
                <h2 className={`text-4xl md:text-5xl font-black mb-5 ${dk?'text-white':'text-gray-900'}`} style={{fontFamily:'Sora,sans-serif'}}>Trusted by governments<br/>across the nation</h2>
                <p className={`text-lg max-w-lg mx-auto ${dk?'text-gray-400':'text-gray-600'}`}>Real results from real agencies — not marketing copy.</p>
              </div>
            </div>

            {/* Row 1: scrolls left */}
            <div className="testi-track mb-5" style={{'--fade-color': fadeColor}}>
              <div className="testi-belt go-left pl-5">
                {[...TESTIMONIALS_RAW, ...TESTIMONIALS_RAW].map((t,i)=>(
                  <TestiCard key={i} t={t} dk={dk}/>
                ))}
              </div>
            </div>

            {/* Row 2: scrolls right (slice reversed) */}
            <div className="testi-track" style={{'--fade-color': fadeColor}}>
              <div className="testi-belt go-right pl-5">
                {[...[...TESTIMONIALS_RAW].reverse(), ...[...TESTIMONIALS_RAW].reverse()].map((t,i)=>(
                  <TestiCard key={i} t={t} dk={dk}/>
                ))}
              </div>
            </div>
          </section>

          {/* ════ SECURITY ════ */}
          <section id="security" className="py-28 px-6">
            <div className="max-w-6xl mx-auto">
              <div className="reveal up text-center mb-16">
                <div className="sdiv"/>
                <p className={`text-xs uppercase tracking-widest font-semibold mb-3 ${dk?'text-violet-400':'text-violet-600'}`} style={{fontFamily:'Sora,sans-serif'}}>Security & Compliance</p>
                <h2 className={`text-4xl md:text-5xl font-black mb-5 ${dk?'text-white':'text-gray-900'}`} style={{fontFamily:'Sora,sans-serif'}}>Built for government.<br/>Secured to government standards.</h2>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5 mb-14">
                {SECURITY_ITEMS.map((s,i)=>(
                  <div key={i} className={`reveal left glass-card rounded-2xl border p-7 flex gap-5 no-hover sd${i+1} ${dk?'bg-white/5 border-white/8':'bg-white/70 border-gray-200/50'}`}
                    style={{boxShadow:dk?'0 4px 24px rgba(0,0,0,.2)':'0 4px 20px rgba(108,99,255,.05)'}}>
                    <span className="text-3xl flex-shrink-0">{s.icon}</span>
                    <div>
                      <h3 className={`font-bold text-sm mb-1.5 ${dk?'text-white':'text-gray-900'}`} style={{fontFamily:'Sora,sans-serif'}}>{s.title}</h3>
                      <p className={`text-xs leading-relaxed ${dk?'text-gray-400':'text-gray-600'}`}>{s.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
              <div className="reveal up flex flex-wrap justify-center gap-3">
                {['FedRAMP Ready','SOC 2 Type II','CJIS Compliant','NIST 800-53','ADA / WCAG 2.1','FIPS 140-2'].map((b,i)=>(
                  <span key={i} className={`px-5 py-2.5 rounded-xl border text-xs font-bold uppercase tracking-wider ${dk?'bg-white/6 border-white/10 text-gray-300':'bg-white/80 border-gray-200 text-gray-600'}`} style={{fontFamily:'Sora,sans-serif'}}>{b}</span>
                ))}
              </div>
            </div>
          </section>

          {/* ════ ABOUT — rotating messages ════ */}
          <section id="about" className={`py-28 px-6 ${dk?'bg-white/2':'bg-gray-50/50'}`}>
            <div className="max-w-6xl mx-auto">
              <div className="reveal up text-center mb-16">
                <div className="sdiv"/>
                <p className={`text-xs uppercase tracking-widest font-semibold mb-3 ${dk?'text-violet-400':'text-violet-600'}`} style={{fontFamily:'Sora,sans-serif'}}>Our Story</p>
                <h2 className={`text-4xl md:text-5xl font-black mb-5 ${dk?'text-white':'text-gray-900'}`} style={{fontFamily:'Sora,sans-serif'}}>We were tired of<br/>slow government software</h2>
                <p className={`text-lg max-w-2xl mx-auto ${dk?'text-gray-400':'text-gray-600'}`}>Botable was founded by former Accela engineers who experienced the pain firsthand.</p>
              </div>

              {/* Mission quote */}
              <div className={`reveal scale glass-card no-hover rounded-3xl border p-10 mb-14 text-center ${dk?'bg-gradient-to-br from-violet-900/30 to-indigo-900/20 border-violet-500/20':'bg-gradient-to-br from-violet-50 to-indigo-50/40 border-violet-200/60'}`}
                style={{boxShadow:dk?'0 16px 50px rgba(108,99,255,.2)':'0 16px 50px rgba(108,99,255,.08)'}}>
                <p className={`text-xl md:text-2xl font-semibold italic leading-relaxed max-w-3xl mx-auto ${dk?'text-gray-200':'text-gray-800'}`} style={{fontFamily:'Sora,sans-serif'}}>
                  "Our mission is to make government services as fast, transparent, and delightful as the best private-sector apps — one bot at a time."
                </p>
                <p className={`mt-5 text-sm font-bold ${dk?'text-violet-400':'text-violet-600'}`} style={{fontFamily:'Sora,sans-serif'}}>— Alexandra Chen, CEO</p>
              </div>

              {/* Team cards with rotating messages */}
              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-5">
                {TEAM.map((m,i)=>(
                  <div key={i} className={`reveal up glass-card rounded-3xl border p-6 sd${i+1} ${dk?'bg-white/5 border-white/8':'bg-white/80 border-gray-200/60'}`}
                    style={{boxShadow:dk?'0 8px 32px rgba(0,0,0,.2)':'0 8px 28px rgba(108,99,255,.07)'}}>
                    <div className={`w-14 h-14 mx-auto rounded-2xl bg-gradient-to-br ${m.color} flex items-center justify-center text-white font-black text-lg mb-4`}
                      style={{fontFamily:'Sora,sans-serif'}}>{m.avatar}</div>
                    <p className={`font-bold text-sm mb-0.5 text-center ${dk?'text-white':'text-gray-900'}`} style={{fontFamily:'Sora,sans-serif'}}>{m.name}</p>
                    <p className={`text-xs font-semibold mb-2 text-center ${dk?'text-violet-400':'text-violet-600'}`}>{m.role}</p>
                    <p className={`text-xs leading-relaxed text-center ${dk?'text-gray-500':'text-gray-500'}`}>{m.bio}</p>
                    {/* Rotating message box */}
                    <RotatingMessages member={m} dk={dk}/>
                  </div>
                ))}
              </div>
            </div>
          </section>

          {/* ════ SUPPORT ════ */}
          <section id="support" className="py-28 px-6">
            <div className="max-w-5xl mx-auto">
              <div className="reveal up text-center mb-16">
                <div className="sdiv"/>
                <p className={`text-xs uppercase tracking-widest font-semibold mb-3 ${dk?'text-violet-400':'text-violet-600'}`} style={{fontFamily:'Sora,sans-serif'}}>Support & Help</p>
                <h2 className={`text-4xl md:text-5xl font-black mb-5 ${dk?'text-white':'text-gray-900'}`} style={{fontFamily:'Sora,sans-serif'}}>We're here when you need us</h2>
              </div>
              <div className="reveal up grid grid-cols-1 md:grid-cols-3 gap-5 mb-16">
                {[
                  {icon:'💬',title:'Live Chat',desc:'Mon–Fri, 7am–7pm ET. Average first response under 3 minutes.',tag:'Fastest'},
                  {icon:'📧',title:'Email Support',desc:'support@botable.com — all plans respond within 24 hours.',tag:'All Plans'},
                  {icon:'📞',title:'Dedicated CSM',desc:'Enterprise and Pro plans get a named Customer Success Manager.',tag:'Pro & Enterprise'},
                ].map((c,i)=>(
                  <div key={i} className={`glass-card rounded-3xl border p-7 text-center sd${i+1} ${dk?'bg-white/5 border-white/8':'bg-white/70 border-gray-200/50'}`}
                    style={{boxShadow:dk?'0 8px 32px rgba(0,0,0,.2)':'0 8px 28px rgba(108,99,255,.06)'}}>
                    <div className="text-4xl mb-4">{c.icon}</div>
                    <span className={`text-xs px-3 py-1 rounded-full font-semibold ${dk?'bg-violet-900/50 text-violet-300':'bg-violet-100 text-violet-700'}`} style={{fontFamily:'Sora,sans-serif'}}>{c.tag}</span>
                    <h3 className={`font-bold text-base mt-4 mb-2 ${dk?'text-white':'text-gray-900'}`} style={{fontFamily:'Sora,sans-serif'}}>{c.title}</h3>
                    <p className={`text-sm ${dk?'text-gray-400':'text-gray-600'}`}>{c.desc}</p>
                  </div>
                ))}
              </div>
              <div className="reveal up space-y-3">
                <h3 className={`text-xl font-bold mb-6 ${dk?'text-white':'text-gray-900'}`} style={{fontFamily:'Sora,sans-serif'}}>Frequently Asked Questions</h3>
                {FAQS.map((f,i)=>(
                  <div key={i} className={`glass-card no-hover rounded-2xl border overflow-hidden ${dk?'bg-white/5 border-white/8':'bg-white/80 border-gray-200/50'}`}>
                    <button onClick={()=>setOpenFaq(openFaq===i?null:i)}
                      className={`w-full flex items-center justify-between p-6 text-left font-semibold text-sm ${dk?'text-gray-200 hover:text-white':'text-gray-800 hover:text-gray-900'}`}
                      style={{fontFamily:'Sora,sans-serif'}}>
                      {f.q}
                      <span className={`ml-4 flex-shrink-0 text-lg leading-none transition-transform duration-300 ${openFaq===i?'rotate-45':''} ${dk?'text-violet-400':'text-violet-600'}`}>+</span>
                    </button>
                    <div className="faq-body" style={{maxHeight:openFaq===i?'180px':'0',opacity:openFaq===i?1:0}}>
                      <p className={`px-6 pb-6 text-sm leading-relaxed ${dk?'text-gray-400':'text-gray-600'}`}>{f.a}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </section>

          {/* ════ CTA BAND ════ */}
          <section className="py-24 px-6">
            <div className="max-w-4xl mx-auto">
              <div className={`reveal scale cta-glow glass-card no-hover rounded-3xl border p-14 text-center ${
                dk?'bg-gradient-to-br from-violet-900/40 via-indigo-900/30 to-purple-900/30 border-violet-500/25'
                  :'bg-gradient-to-br from-violet-600 to-indigo-600 border-violet-400/30'
              }`}>
                <p className={`text-xs uppercase tracking-widest font-semibold mb-4 ${dk?'text-violet-300':'text-violet-200'}`} style={{fontFamily:'Sora,sans-serif'}}>Ready to automate?</p>
                <h2 className="text-4xl md:text-5xl font-black mb-5 text-white" style={{fontFamily:'Sora,sans-serif'}}>Start for free.<br/>Scale when ready.</h2>
                <p className={`text-lg mb-10 max-w-lg mx-auto ${dk?'text-gray-300':'text-violet-100'}`}>Join 50+ agencies already saving thousands of staff hours every month.</p>
                <div className="flex flex-col sm:flex-row gap-4 justify-center">
                  <Link to="/signup" className="btn-glow inline-flex items-center gap-2 py-4 px-10 bg-white text-violet-700 font-bold text-base rounded-2xl hover:scale-[1.04] active:scale-95 shadow-2xl transition-all" style={{fontFamily:'Sora,sans-serif'}}>
                    🚀 Get Started Free
                  </Link>
                  <button onClick={()=>scrollTo('support')} className="inline-flex items-center gap-2 py-4 px-10 font-semibold text-base rounded-2xl border-2 border-white/30 text-white hover:bg-white/10 hover:scale-[1.03] transition-all" style={{fontFamily:'Sora,sans-serif'}}>
                    Talk to Sales →
                  </button>
                </div>
              </div>
            </div>
          </section>

        </main>

        {/* ════ FOOTER ════ */}
        <footer className={`relative z-10 border-t transition-colors duration-500 ${dk?'border-white/6 bg-[#060612]/90':'border-gray-200/60 bg-white/60'} backdrop-blur-xl`}>
          <div className="max-w-6xl mx-auto px-6 py-16">
            <div className="grid grid-cols-2 md:grid-cols-5 gap-10 mb-12">
              <div className="col-span-2">
                <p className={`text-2xl font-black mb-3 ${dk?'grad-dk':'grad'}`} style={{fontFamily:'Sora,sans-serif'}}>BOTABLE</p>
                <p className={`text-sm leading-relaxed max-w-xs ${dk?'text-gray-500':'text-gray-500'}`}>Intelligent automation for modern government agencies. Built on Accela, designed for citizens.</p>
                <div className="flex gap-3 mt-5">
                  {['𝕏','in','gh'].map((s,i)=>(
                    <button key={i} className={`w-9 h-9 rounded-xl border text-xs font-bold transition-all hover:scale-110 ${dk?'border-gray-700 text-gray-500 hover:border-violet-500 hover:text-violet-400':'border-gray-200 text-gray-400 hover:border-violet-400 hover:text-violet-600'}`}>{s}</button>
                  ))}
                </div>
              </div>
              {[
                {title:'Product',links:['BotBuilder','FlowEngine','InsightBoard','NotifyIQ','CitizenPortal']},
                {title:'Company',links:['About','Careers','Blog','Press','Partners']},
                {title:'Support',links:['Documentation','API Reference','Status Page','Community','Contact']},
              ].map((col,i)=>(
                <div key={i}>
                  <p className={`text-xs font-bold uppercase tracking-widest mb-4 ${dk?'text-gray-400':'text-gray-500'}`} style={{fontFamily:'Sora,sans-serif'}}>{col.title}</p>
                  <ul className="space-y-2.5">
                    {col.links.map((l,j)=>(
                      <li key={j}><a href="#" className={`text-sm transition-colors ${dk?'text-gray-500 hover:text-violet-400':'text-gray-500 hover:text-violet-600'}`}>{l}</a></li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
            <div className={`border-t pt-8 flex flex-col md:flex-row items-center justify-between gap-3 text-xs ${dk?'border-white/6 text-gray-600':'border-gray-100 text-gray-400'}`}>
              <p>© 2025 Botable, Inc. All rights reserved.</p>
              <p>1234 Tech Lane, Innovation City, IC 56789 · contact@botable.com · (123) 456-7890</p>
              <div className="flex gap-5">
                {['Privacy','Terms','Cookies'].map(l=>(
                  <a key={l} href="#" className={`hover:underline ${dk?'hover:text-gray-300':'hover:text-gray-600'}`}>{l}</a>
                ))}
              </div>
            </div>
          </div>
        </footer>

      </div>
    </>
  );
}